import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import { type BaseTextAttributes, isAgentOrigin } from '@blocksuite/store';
import { batch, effect } from '@preact/signals-core';
import { html, render } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import * as Y from 'yjs';

import type { VLine } from '../components/v-line.js';
import type { InlineEditor } from '../inline-editor.js';
import type { InlineRange } from '../types.js';
import { deltaInsertsToChunks } from '../utils/delta-convert.js';

export class RenderService<TextAttributes extends BaseTextAttributes> {
  private readonly _onYTextChange = (
    event: Y.YTextEvent,
    transaction: Y.Transaction
  ) => {
    this.editor.slots.textChange.next();

    const yText = this.editor.yText;

    if (yText.toString().includes('\r')) {
      throw new BlockSuiteError(
        ErrorCode.InlineEditorError,
        'yText must not contain "\\r" because it will break the range synchronization'
      );
    }

    const hidden = this.editor.revealService.hidden$.peek();
    batch(() => {
      if (isAgentOrigin(transaction.origin)) {
        const origin = transaction.origin;
        let index = 0;
        for (const delta of event.delta) {
          if (delta.retain) index += delta.retain;
          if (typeof delta.insert === 'string') {
            this.editor.revealService.reveal({
              index,
              length: delta.insert.length,
              seed: [origin.runId, origin.stepId].join(':'),
              runId: origin.runId,
            });
            index += delta.insert.length;
          }
        }
      } else if (transaction.local) this.editor.revealService.finish();
    });
    if (this.editor.revealService.hidden$.peek() === hidden) this.render();

    const inlineRange = this.editor.inlineRange$.peek();
    if (!inlineRange || transaction.local) return;

    const lastStartRelativePosition = this.editor.lastStartRelativePosition;
    const lastEndRelativePosition = this.editor.lastEndRelativePosition;
    if (!lastStartRelativePosition || !lastEndRelativePosition) return;

    const doc = this.editor.yText.doc;
    if (!doc) {
      console.error('doc is not found when syncing yText');
      return;
    }
    const absoluteStart = Y.createAbsolutePositionFromRelativePosition(
      lastStartRelativePosition,
      doc
    );
    const absoluteEnd = Y.createAbsolutePositionFromRelativePosition(
      lastEndRelativePosition,
      doc
    );

    const startIndex = absoluteStart?.index;
    const endIndex = absoluteEnd?.index;
    if (!startIndex || !endIndex) return;

    const newInlineRange: InlineRange = {
      index: startIndex,
      length: endIndex - startIndex,
    };
    if (!this.editor.isValidInlineRange(newInlineRange)) return;

    this.editor.setInlineRange(newInlineRange);
    this.editor.syncInlineRange();
  };

  mount = () => {
    const editor = this.editor;
    const yText = editor.yText;

    editor.disposables.add(
      effect(() => {
        editor.revealService.hidden$.value;
        if (editor.mounted) this.render(false);
      })
    );
    yText.observe(this._onYTextChange);
    editor.disposables.add({
      dispose: () => {
        yText.unobserve(this._onYTextChange);
      },
    });
  };

  private _rendering = false;
  get rendering() {
    return this._rendering;
  }
  // render current deltas to VLines
  render = (syncRange = true) => {
    if (!this.editor.rootElement) return;

    this._rendering = true;

    const rootElement = this.editor.rootElement;
    const embedDeltas = this.editor.deltaService.embedDeltas;
    const chunks = deltaInsertsToChunks(embedDeltas);
    const hidden = this.editor.revealService.hiddenIndexRanges();

    let deltaIndex = 0;
    // every chunk is a line
    const lines = chunks.map((chunk, lineIndex) => {
      if (lineIndex > 0) {
        deltaIndex += 1; // for '\n'
      }

      const lineStartOffset = deltaIndex;
      if (chunk.length > 0) {
        const elements: VLine['elements'] = chunk.flatMap(delta => {
          const deltaStart = deltaIndex;
          const deltaEnd = deltaStart + delta.insert.length;
          const cuts = new Set([deltaStart, deltaEnd]);
          for (const range of hidden) {
            if (range.from > deltaStart && range.from < deltaEnd)
              cuts.add(range.from);
            if (range.to > deltaStart && range.to < deltaEnd)
              cuts.add(range.to);
          }
          const boundaries = [...cuts].sort((a, b) => a - b);
          return boundaries
            .slice(1)
            .map((endOffset, part): VLine['elements'][number] => {
              const startOffset = boundaries[part];
              const fragment = {
                ...delta,
                insert: delta.insert.slice(
                  startOffset - deltaStart,
                  endOffset - deltaStart
                ),
              };
              deltaIndex = endOffset;
              return [
                html`<v-element
                  .inlineEditor=${this.editor}
                  .delta=${{
                    insert: fragment.insert,
                    attributes:
                      this.editor.attributeService.normalizeAttributes(
                        delta.attributes
                      ),
                  }}
                  .hidden=${hidden.some(
                    range => range.from <= startOffset && range.to >= endOffset
                  )}
                  .startOffset=${startOffset}
                  .endOffset=${endOffset}
                  .lineIndex=${lineIndex}
                ></v-element>`,
                fragment,
              ];
            });
        });

        return html`<v-line
          .elements=${elements}
          .index=${lineIndex}
          .startOffset=${lineStartOffset}
          .endOffset=${deltaIndex}
        ></v-line>`;
      } else {
        return html`<v-line
          .elements=${[]}
          .index=${lineIndex}
          .startOffset=${lineStartOffset}
          .endOffset=${deltaIndex}
        ></v-line>`;
      }
    });

    try {
      render(
        repeat(
          lines.map((line, i) => ({ line, index: i })),
          entry => entry.index,
          entry => entry.line
        ),
        rootElement
      );
    } catch {
      // Lit may be crashed by IME input and we need to rerender whole editor for it
      this.editor.rerenderWholeEditor();
    }

    this.editor
      .waitForUpdate()
      .then(() => {
        this._rendering = false;
        this.editor.slots.renderComplete.next();
        // Animation frames preserve an active caret but never focus another editor.
        if (
          syncRange ||
          rootElement.ownerDocument.activeElement === rootElement
        )
          this.editor.syncInlineRange();
      })
      .catch(console.error);
  };

  rerenderWholeEditor = () => {
    const rootElement = this.editor.rootElement;

    if (!rootElement || !rootElement.isConnected) return;

    rootElement.replaceChildren();
    // Because we bypassed Lit and disrupted the DOM structure, this will cause an inconsistency in the original state of `ChildPart`.
    // Therefore, we need to remove the original `ChildPart`.
    // https://github.com/lit/lit/blob/a2cd76cfdea4ed717362bb1db32710d70550469d/packages/lit-html/src/lit-html.ts#L2248

    delete (rootElement as any)['_$litPart$'];
    this.render();
  };

  waitForUpdate = async () => {
    if (!this.editor.rootElement) return;
    const vLines = Array.from(
      this.editor.rootElement.querySelectorAll('v-line')
    );
    await Promise.all(vLines.map(line => line.updateComplete));
  };

  constructor(readonly editor: InlineEditor<TextAttributes>) {}
}
